import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Download, FileDown, Loader2, RefreshCw, Users } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '@/context/AuthContext';
import apiClient, { API_BASE } from '@/config/api';
import { Button } from '@/components/ui/button';
import { usePdfExport, filenameFromDisposition } from '@/hooks/usePdfExport';
import { cn } from '@/lib/utils';

/**
 * Day-out-of-days grid (M2.2). Pure read of GET /films/{id}/reports/dood.
 * Rows = cast (busiest first), columns = shoot days, cells = SW/W/H/WF/SWF.
 */
const CODE_CLS = {
  SW: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  WF: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  SWF: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  W: 'bg-green-500/15 text-green-700 dark:text-green-300',
  H: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
};

export default function DoodGrid({ filmId, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csvBusy, setCsvBusy] = useState(false);
  const { exportPdf, exporting } = usePdfExport();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/films/${filmId}/reports/dood`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load the day-out-of-days');
    } finally {
      setLoading(false);
    }
  }, [filmId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const downloadCsv = async () => {
    setCsvBusy(true);
    try {
      const res = await apiClient.get(`/films/${filmId}/reports/dood.csv`, { responseType: 'blob' });
      const name = filenameFromDisposition(res.headers?.['content-disposition'], 'Day out of days.csv');
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error('Could not download the CSV');
    } finally {
      setCsvBusy(false);
    }
  };

  if (loading && !data) {
    return <div className="h-40 rounded-2xl bg-muted/50 animate-pulse" />;
  }
  if (!data) return null;

  const { days, cast, totals, codes } = data;

  return (
    <div className="space-y-3" data-testid="dood-grid">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-muted-foreground flex-1 min-w-[12rem]">
          <Users className="size-3 inline mr-1" />
          {totals.cast} cast · {totals.days} shoot {totals.days === 1 ? 'day' : 'days'} · {totals.work_days} work · {totals.hold_days} hold
          {totals.cast_with_unscheduled_scenes > 0 && (
            <span className="text-destructive"> · {totals.cast_with_unscheduled_scenes} with unscheduled scenes</span>
          )}
        </p>
        <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={load} disabled={loading}>
          <RefreshCw className={cn('size-3.5 mr-1', loading && 'animate-spin')} /> Refresh
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={downloadCsv} disabled={csvBusy} data-testid="dood-csv">
          {csvBusy ? <Loader2 className="size-3.5 mr-1 animate-spin" /> : <Download className="size-3.5 mr-1" />} CSV
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={exporting} data-testid="dood-pdf"
          onClick={() => exportPdf(`/films/${filmId}/reports/dood.pdf`, { kind: 'dood', fallbackName: 'Day out of days.pdf' })}>
          <FileDown className="size-3.5 mr-1" /> PDF
        </Button>
      </div>

      {days.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No shoot days yet. Build days on the board first — the day-out-of-days fills in from there.
        </div>
      ) : cast.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No cast on this film yet. Add cast members and tag them on scenes or shots.
        </div>
      ) : (
        <div className="rounded-2xl border border-border overflow-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead className="bg-muted/50 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-muted/50 backdrop-blur z-20 min-w-[10rem]">Cast</th>
                {days.map((d) => (
                  <th key={d.id} className="px-1.5 py-2 font-medium text-center min-w-[3rem] align-bottom" title={d.name}>
                    <div className="truncate max-w-[4.5rem] mx-auto">{d.name.split(' — ')[0]}</div>
                    {d.date && <div className="text-[10px] text-muted-foreground font-normal">{d.date.slice(5)}</div>}
                  </th>
                ))}
                <th className="px-2 py-2 text-center font-semibold" title="Work days">W</th>
                <th className="px-2 py-2 text-center font-semibold" title="Hold days">H</th>
                <th className="px-2 py-2 text-center font-semibold" title="Total paid days">Total</th>
                {totals.cast_with_unscheduled_scenes > 0 && <th className="px-2 py-2 text-left font-medium text-muted-foreground">Not yet scheduled</th>}
              </tr>
            </thead>
            <tbody>
              {cast.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-accent/30" data-testid={`dood-row-${r.id}`}>
                  <td className="px-3 py-1.5 sticky left-0 bg-card z-10 whitespace-nowrap">
                    <span className="font-semibold">{r.name}</span>
                    {r.role && <span className="text-muted-foreground ml-1.5">{r.role}</span>}
                  </td>
                  {r.cells.map((c, i) => (
                    <td key={days[i].id} className="px-1 py-1 text-center">
                      {c.code ? (
                        <span
                          className={cn('inline-block min-w-[2.2rem] px-1 py-0.5 rounded font-bold tabular-nums', CODE_CLS[c.code])}
                          title={c.scenes.length ? `Sc ${c.scenes.join(', ')}` : codes[c.code]}
                        >
                          {c.code}
                        </span>
                      ) : null}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-center tabular-nums font-semibold">{r.work_days}</td>
                  <td className="px-2 py-1.5 text-center tabular-nums font-semibold">{r.hold_days}</td>
                  <td className="px-2 py-1.5 text-center tabular-nums font-bold">{r.total_days}</td>
                  {totals.cast_with_unscheduled_scenes > 0 && (
                    <td className="px-2 py-1.5 text-destructive whitespace-nowrap">{r.unscheduled_scenes.length ? `Sc ${r.unscheduled_scenes.join(', ')}` : ''}</td>
                  )}
                </tr>
              ))}
              <tr className="border-t border-border bg-muted/40 text-muted-foreground">
                <td className="px-3 py-1.5 sticky left-0 bg-muted/40 backdrop-blur font-medium">Cast working</td>
                {days.map((d) => <td key={d.id} className="px-1 py-1.5 text-center tabular-nums">{d.headcount || ''}</td>)}
                <td colSpan={totals.cast_with_unscheduled_scenes > 0 ? 4 : 3} />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
        {Object.entries(codes).map(([k, v]) => (
          <span key={k}><b className={cn('inline-block min-w-[1.8rem] text-center rounded px-1 mr-1', CODE_CLS[k])}>{k}</b>{v}</span>
        ))}
      </div>
    </div>
  );
}
